import * as yup from 'yup'

import {
  CalculationInput,
  ColumnStatus,
  ResultsUnitsAmount,
  ResultsUnitsPeriod,
  Units,
} from 'types/graphql/schema'

// Investment column on/off statuses live in React state in Index.tsx (not
// Formik values) and are merged into the GraphQL payload by valuesToCalcInput,
// so they are intentionally not part of validationSchema.

const herdSumErrMsg = 'Herd structure must sum to 100'
export const validationSchema = yup.object().shape({
  units: yup.string().oneOf(Object.values(Units)).required(),
  resultsUnitsAmount: yup.string().oneOf(Object.values(ResultsUnitsAmount)).required(),
  resultsUnitsPeriod: yup.string().oneOf(Object.values(ResultsUnitsPeriod)).required(),
  currencyId: yup.string().required(),
  herdSize: yup.number().min(0).integer().required('Required'),
  aabCents: yup.number().required('Required'),
  isPlusDmi: yup.boolean().required(),
  isAab: yup.boolean().required(),
  productId: yup.string().required(),
  productPrice: yup.number().min(0).required(),
  regionId: yup.string().required(),

  // Investment section
  investmentHerdStructureFarOff: yup.number().min(0).required(),
  investmentHerdStructureCloseUp: yup.number().min(0).required(),
  investmentHerdStructureFresh: yup.number().min(0).required(),
  investmentHerdStructureEarlyMidLact: yup.number().min(0).required(),
  investmentHerdStructureLateLact: yup.number().min(0).required(),

  investmentProductGramsPerCowDayCloseUp: yup.number().min(0).required(),
  investmentProductGramsPerCowDayFresh: yup.number().min(0).required(),
  investmentProductGramsPerCowDayEarlyMidLact: yup.number().min(0).required(),
  investmentProductGramsPerCowDayLateLact: yup.number().min(0).required(),

  investmentDmiFarOff: yup.number().min(0).required(),
  investmentDmiCloseUp: yup.number().min(0).required(),
  investmentDmiFresh: yup.number().min(0).required(),
  investmentDmiEarlyMidLact: yup.number().min(0).required(),
  investmentDmiLateLact: yup.number().min(0).required(),

  investmentDmCostCurrentFarOff: yup.number().min(0).required(),
  investmentDmCostCurrentCloseUp: yup.number().min(0).required(),
  investmentDmCostCurrentFresh: yup.number().min(0).required(),
  investmentDmCostCurrentEarlyMidLact: yup.number().min(0).required(),
  investmentDmCostCurrentLateLact: yup.number().min(0).required(),

  // Short term returns section
  shortTermReturnsMilkCurrent: yup.number().min(0).required(),
  shortTermReturnsMilkDifference: yup.number().min(0).required(),
  shortTermReturnsMilkFmmoPrice: yup.number().min(0).required(),

  shortTermReturnsFatCurrent: yup.number().min(0).required(),
  shortTermReturnsFatDifference: yup.number().min(0).required(),
  shortTermReturnsFatFmmoPrice: yup.number().min(0).required(),

  shortTermReturnsProteinCurrent: yup.number().min(0).required(),
  shortTermReturnsProteinDifference: yup.number().min(0).required(),
  shortTermReturnsProteinFmmoPrice: yup.number().min(0).required(),

  shortTermReturnsOtherSolidsCurrent: yup.number().min(0).required(),
  shortTermReturnsOtherSolidsDifference: yup.number().min(0).required(),
  shortTermReturnsOtherSolidsFmmoPrice: yup.number().min(0).required(),

  shortTermReturnsPremiumFmmoPrice: yup.number().min(0).required(),

  // Long term returns reproduction section
  longTermReturnsReproductionCostCurrent: yup.number().min(0).required(),
  longTermReturnsReplacementCostCurrent: yup.number().min(0).required(),
  longTermReturnsCullValueCurrent: yup.number().min(0).required(),
  longTermReturnsCalfValueCurrent: yup.number().min(0).required(),
  longTermReturnsLastMonthToBreedCurrent: yup.number().min(0).required(),
  longTermReturnsMinimumMilkToBreedCurrent: yup.number().min(0).required(),
  longTermReturnsCullingRateCurrent: yup.number().min(0).required(),
  longTermReturnsPregnancyRateCurrent: yup.number().min(0).required(),
  longTermReturnsPregnancyRateDifference: yup.number().required(),
  longTermReturnsPregnancyLossCurrent: yup.number().min(0).required(),
  longTermReturnsPregnancyLossDifference: yup.number().required(),

  // Long term returns health section
  longTermReturnsMastitisRegular: yup.number().min(0).required(),
  longTermReturnsMastitisReduced: yup.number().min(0).required(),
  longTermReturnsMastitisCostPerCase: yup.number().min(0).required(),
  longTermReturnsKetosisClinicalRegular: yup.number().min(0).required(),
  longTermReturnsKetosisClinicalReduced: yup.number().min(0).required(),
  longTermReturnsKetosisClinicalCostPerCase: yup.number().min(0).required(),
  longTermReturnsKetosisSubClinicalRegular: yup.number().min(0).required(),
  longTermReturnsKetosisSubClinicalReduced: yup.number().min(0).required(),
  longTermReturnsKetosisSubclinicalCostPerCase: yup.number().min(0).required(),
  longTermReturnsMetritisRegular: yup.number().min(0).required(),
  longTermReturnsMetritisReduced: yup.number().min(0).required(),
  longTermReturnsMetritisCostPerCase: yup.number().min(0).required(),
})
  .test('herd-structure-sum', herdSumErrMsg, validateHerdStructure('investmentHerdStructureFarOff'))
  .test('herd-structure-sum', herdSumErrMsg, validateHerdStructure('investmentHerdStructureCloseUp'))
  .test('herd-structure-sum', herdSumErrMsg, validateHerdStructure('investmentHerdStructureFresh'))
  .test('herd-structure-sum', herdSumErrMsg, validateHerdStructure('investmentHerdStructureEarlyMidLact'))
  .test('herd-structure-sum', herdSumErrMsg, validateHerdStructure('investmentHerdStructureLateLact'))

function validateHerdStructure(field: string) {
  return function (values: any, context: yup.TestContext) {
    const sum = (
      values.investmentHerdStructureFarOff +
      values.investmentHerdStructureCloseUp +
      values.investmentHerdStructureFresh +
      values.investmentHerdStructureEarlyMidLact +
      values.investmentHerdStructureLateLact
    )
    if (sum !== 100) {
      return context.createError({path: field})
    }
    return true
  }
}

export const schemaDescription = validationSchema.describe()
export type valuesType = yup.InferType<typeof validationSchema>

// CalculationInput minus the investment column statuses (see comment above):
// the set of fields the Formik values are expected to cover.
type InvestmentColumnStatusField =
  | 'investmentFarOffStatus'
  | 'investmentCloseUpStatus'
  | 'investmentFreshStatus'
  | 'investmentEarlyMidLactStatus'
  | 'investmentLateLactStatus'
type CalculationInputValues = Omit<CalculationInput, InvestmentColumnStatusField>

// Compile-time guarantee that valuesType covers exactly the non-status fields of
// CalculationInput. If a field is added or removed on either side without a
// matching change on the other, one of these aliases fails to compile.
type AssertTrue<T extends true> = T
type Covers<A, B> = A extends B ? true : false
type _ValuesCoverInput = AssertTrue<Covers<valuesType, CalculationInputValues>>
type _InputCoversValues = AssertTrue<Covers<CalculationInputValues, valuesType>>


function toFixed(value: number, decimal: number) {
  if (typeof value === 'string') {
    value = parseFloat(value)
  }
  return value.toFixed(decimal) as unknown as number
}


export function renderValues(values: valuesType, currencyCode: string) {
  return {
    ...values,
    investmentDmiFarOff: toFixed(values.investmentDmiFarOff, 1),
    investmentDmiCloseUp: toFixed(values.investmentDmiCloseUp, 1),
    investmentDmiFresh: toFixed(values.investmentDmiFresh, 1),
    investmentDmiEarlyMidLact: toFixed(values.investmentDmiEarlyMidLact, 1),
    investmentDmiLateLact: toFixed(values.investmentDmiLateLact, 1),
    investmentDmCostCurrentFarOff: toFixed(values.investmentDmCostCurrentFarOff, 3),
    investmentDmCostCurrentCloseUp: toFixed(values.investmentDmCostCurrentCloseUp, 3),
    investmentDmCostCurrentFresh: toFixed(values.investmentDmCostCurrentFresh, 3),
    investmentDmCostCurrentEarlyMidLact: toFixed(values.investmentDmCostCurrentEarlyMidLact, 3),
    investmentDmCostCurrentLateLact: toFixed(values.investmentDmCostCurrentLateLact, 3),
    shortTermReturnsMilkCurrent: toFixed(values.shortTermReturnsMilkCurrent, 1),
    shortTermReturnsMilkDifference: toFixed(values.shortTermReturnsMilkDifference, 2),
    shortTermReturnsMilkFmmoPrice: toFixed(values.shortTermReturnsMilkFmmoPrice, 4),
    shortTermReturnsFatFmmoPrice: toFixed(values.shortTermReturnsFatFmmoPrice, 4),
    shortTermReturnsProteinFmmoPrice: toFixed(values.shortTermReturnsProteinFmmoPrice, 4),
    shortTermReturnsOtherSolidsFmmoPrice: toFixed(values.shortTermReturnsOtherSolidsFmmoPrice, 4),
    shortTermReturnsPremiumFmmoPrice: toFixed(values.shortTermReturnsPremiumFmmoPrice, 4),
    longTermReturnsCullValueCurrent: toFixed(values.longTermReturnsCullValueCurrent, 3),
    longTermReturnsMinimumMilkToBreedCurrent: toFixed(values.longTermReturnsMinimumMilkToBreedCurrent, 0),

    productPrice: toFixed(values.productPrice, currencyCode === 'JPY' ? 0 : 2),
    longTermReturnsCalfValueCurrent: toFixed(values.longTermReturnsCalfValueCurrent, 0),
    longTermReturnsReproductionCostCurrent: toFixed(values.longTermReturnsReproductionCostCurrent, 0),
    longTermReturnsReplacementCostCurrent: toFixed(values.longTermReturnsReplacementCostCurrent, 0),
    longTermReturnsMastitisCostPerCase: toFixed(values.longTermReturnsMastitisCostPerCase, 0),
    longTermReturnsKetosisClinicalCostPerCase: toFixed(values.longTermReturnsKetosisClinicalCostPerCase, 0),
    longTermReturnsKetosisSubclinicalCostPerCase: toFixed(values.longTermReturnsKetosisSubclinicalCostPerCase, 0),
    longTermReturnsMetritisCostPerCase: toFixed(values.longTermReturnsMetritisCostPerCase, 0),
  }
}

export function valuesToCalcInput(values: valuesType, investmentColumnsStatuses: ColumnStatus[]): CalculationInput {
  return {
    ...(
      Object.fromEntries(
        Object.entries(values).map(([k, v]) => {
          // Status fields aren't in the schema (see top of file); they fall
          // through here unchanged and are overwritten by the explicit
          // investment*Status assignments below.
          return [k, schemaDescription.fields[k]?.type === 'number' && typeof v === 'string' ? parseFloat(v) : v]
        })
      )
    ) as unknown as CalculationInput,
    investmentFarOffStatus: investmentColumnsStatuses[0],
    investmentCloseUpStatus: investmentColumnsStatuses[1],
    investmentFreshStatus: investmentColumnsStatuses[2],
    investmentEarlyMidLactStatus: investmentColumnsStatuses[3],
    investmentLateLactStatus: investmentColumnsStatuses[4],
  }
}